import { useCallback, useEffect, useState } from "react";
import { CalendarRange, Loader2, Mail, Send } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const usd = (v, dp = 6) =>
  v == null ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: dp })}`;
const inr = (v) =>
  v == null ? "—" : `\u20B9${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const mins = (v) => (v == null ? "—" : `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 })}m`);

function Delta({ d }) {
  if (!d) return null;
  const up = d.abs >= 0;
  return (
    <span className={`ml-1.5 text-[10px] font-mono ${up ? "text-red-300" : "text-emerald-300"}`}>
      {up ? "▲" : "▼"} {Math.abs(d.pct).toFixed(1)}%
    </span>
  );
}

export default function WeeklyDigest() {
  const [week, setWeek] = useState("last");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/admin/spend/digest?week=${week}`);
      setData(d);
    } catch (err) {
      if (err?.response?.status !== 403) toast.error("Could not load the weekly digest");
    }
  }, [week]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async () => {
    setBusy(true);
    try {
      const { data: s } = await api.post("/admin/spend/digest/settings", {
        enabled: !data.settings.enabled,
      });
      toast.success(s.enabled ? "Weekly digest enabled" : "Weekly digest disabled");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change the setting");
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/spend/digest/send", { week });
      if (r.already_sent) toast.warning(r.detail);
      else toast.success(r.sent ? `Digest for ${r.week.key} emailed` : "Digest claimed but email not configured");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the digest");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const c = data.current;
  const cmp = data.comparison || {};
  const money = data.money;

  return (
    <section data-testid="weekly-digest-panel">
      <h2 className="font-heading text-2xl font-bold tracking-tight mb-3 flex items-center gap-2">
        <CalendarRange className="w-4 h-4 text-blue-400" /> Weekly spend digest
      </h2>

      <div className="border border-zinc-800 bg-zinc-900/40 p-5 space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex gap-px bg-zinc-800 border border-zinc-800" data-testid="digest-week-picker">
            {[
              { k: "last", label: "Last week" },
              { k: "current", label: "This week" },
            ].map((o) => (
              <button
                key={o.k}
                type="button"
                onClick={() => setWeek(o.k)}
                data-testid={`digest-week-${o.k}`}
                className={`h-8 px-3 text-[10px] font-mono uppercase tracking-widest ${
                  week === o.k ? "bg-white text-zinc-950" : "bg-zinc-950 text-zinc-400 hover:bg-zinc-800"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <span className="text-[11px] font-mono text-zinc-500" data-testid="digest-week-label">
            {data.week.key} · {data.week.label}
          </span>

          <div className="flex items-center gap-2 ml-auto">
            <label className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-zinc-400 cursor-pointer">
              <input
                type="checkbox"
                checked={!!data.settings.enabled}
                onChange={toggle}
                disabled={busy}
                data-testid="digest-enabled-toggle"
                className="accent-emerald-500"
              />
              Auto-send weekly
            </label>
            <button
              type="button"
              onClick={send}
              disabled={busy || data.already_sent}
              data-testid="digest-send-btn"
              className="h-9 px-3 text-[10px] font-mono uppercase tracking-widest border border-zinc-800 hover:border-blue-500/50 hover:text-blue-300 inline-flex items-center gap-1.5 disabled:opacity-40"
              title={data.already_sent ? "Already sent — a retry cannot duplicate it" : "Email this digest now"}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              {data.already_sent ? "Already sent" : "Send now"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            ["Total AI spend", <>{usd(c.total_usd, 4)}<Delta d={cmp.total_usd} /></>, "digest-kpi-total"],
            ["Videos", <>{c.videos}<Delta d={cmp.videos} /></>, "digest-kpi-videos"],
            ["Video minutes", <>{mins(c.minutes)}<Delta d={cmp.minutes} /></>, "digest-kpi-minutes"],
            ["AI cost / minute", <>{usd(c.cost_per_minute_usd, 5)}<Delta d={cmp.cost_per_minute_usd} /></>, "digest-kpi-per-minute"],
          ].map(([label, value, tid]) => (
            <div key={label} className="border border-zinc-800 bg-zinc-950/60 p-4" data-testid={tid}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
              <div className="text-xl font-mono text-white mt-1">{value}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="border border-zinc-800" data-testid="digest-provider-table">
            <div className="bg-zinc-900/60 px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
              Provider split
            </div>
            <table className="w-full text-xs">
              <tbody>
                {[
                  ["Whisper (transcription)", c.whisper_usd],
                  ["DeepSeek", c.deepseek_usd],
                  ["Gemini QE", c.gemini_qe_usd],
                  ["GPT-5.4 total", c.gpt54_total_usd],
                  ["· GPT-5.4 escalation", c.gpt54_escalation_usd],
                  ["· GPT-5.4 critic_sentences", c.gpt54_critic_usd],
                ].map(([label, v]) => (
                  <tr key={label} className="border-t border-zinc-800/60">
                    <td className="px-3 py-2 text-zinc-400 font-mono">{label}</td>
                    <td className="px-3 py-2 text-right font-mono text-white">{usd(v, 6)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border border-zinc-800" data-testid="digest-money-table">
            <div className="bg-zinc-900/60 px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
              Money, kept separate
            </div>
            <table className="w-full text-xs">
              <tbody>
                <tr className="border-t border-zinc-800/60">
                  <td className="px-3 py-2 text-zinc-400 font-mono">AI spend</td>
                  <td className="px-3 py-2 text-right font-mono text-white">
                    {usd(money.ai_spend_usd, 4)}
                    {money.ai_spend_inr != null ? ` · ${inr(money.ai_spend_inr)}` : ""}
                  </td>
                </tr>
                <tr className="border-t border-zinc-800/60">
                  <td className="px-3 py-2 text-zinc-400 font-mono">Infrastructure</td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-600">not instrumented</td>
                </tr>
                <tr className="border-t border-zinc-800/60">
                  <td className="px-3 py-2 text-zinc-400 font-mono">Attributed plan revenue</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-300">
                    {inr(money.attributed_plan_revenue_inr)}
                  </td>
                </tr>
                <tr className="border-t border-zinc-800/60">
                  <td className="px-3 py-2 text-zinc-400 font-mono">Cash collected (Cashfree)</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-300">
                    {inr(money.cash_collected_inr)} · {money.cash_transactions || 0} txn
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="border border-zinc-800" data-testid="digest-top-videos">
          <div className="bg-zinc-900/60 px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
            Largest-cost videos · escalations {c.escalation_requests}
            {c.escalation_rate_pct != null
              ? ` (${c.escalation_rate_pct}% of ${c.qe_reviewed_segments} QE segments)`
              : " (no QE segments)"}{" "}
            · failed {c.failed_events} · usage-unavailable {c.usage_unavailable_events}
          </div>
          <table className="w-full text-xs">
            <tbody>
              {(c.top_videos || []).map((v) => (
                <tr key={v.job_id} className="border-t border-zinc-800/60">
                  <td className="px-3 py-2 font-mono text-zinc-300">{v.job_id.slice(0, 18)}</td>
                  <td className="px-3 py-2 text-right text-zinc-400">{mins(v.duration_minutes)}</td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-400">
                    {usd(v.cost_per_minute_usd, 5)}/min
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-white">{usd(v.total_usd, 4)}</td>
                </tr>
              ))}
              {(c.top_videos || []).length === 0 && (
                <tr>
                  <td className="px-3 py-3 text-zinc-500 text-center" colSpan={4} data-testid="digest-no-videos">
                    No videos with cost telemetry in this week.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {c.highest_cost_per_minute && (
            <div className="px-3 py-2 border-t border-zinc-800/60 text-[11px] font-mono text-zinc-500">
              Highest cost per minute: {c.highest_cost_per_minute.job_id} ·{" "}
              {usd(c.highest_cost_per_minute.cost_per_minute_usd, 5)}/min
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-4 text-[10px] font-mono text-zinc-600">
          <span className="inline-flex items-center gap-1.5" data-testid="digest-email-status">
            <Mail className="w-3 h-3" />
            {data.email_configured
              ? `email to ${(data.recipients || []).join(", ")}`
              : "email not configured (Resend key missing)"}
          </span>
          <span data-testid="digest-comparison-note">
            {data.comparison_note || `compared with ${cmp.week}`}
          </span>
          <span>one email per ISO week — claiming is idempotent, retries cannot duplicate it</span>
        </div>

        {(data.history || []).length > 0 && (
          <div className="border border-zinc-800" data-testid="digest-history">
            <div className="bg-zinc-900/60 px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
              Digest history
            </div>
            <table className="w-full text-[11px]">
              <tbody>
                {data.history.map((h) => (
                  <tr key={h.week_key} className="border-t border-zinc-800/60">
                    <td className="px-3 py-1.5 font-mono text-zinc-300">{h.week_key}</td>
                    <td className="px-3 py-1.5 text-zinc-500 font-mono">
                      {h.sent_at ? new Date(h.sent_at).toLocaleString() : "claimed"}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-zinc-400">{usd(h.total_usd, 4)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-zinc-500">
                      {h.sent ? "emailed" : "not emailed"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
